import React from 'react';
import { BookOpen, Building2, Gavel, Globe2, Mail } from 'lucide-react';
import Button from '../components/Button';
import { useTranslations } from '../context/TranslationContext';
import { LEGISGRAPH_METRICS } from '../data/legisgraph';

const OurDataSourcesSection: React.FC = () => {
  const { currentLanguage, t } = useTranslations();
  const isIt = currentLanguage === 'it';
  const metricLabels = isIt
    ? { nodes: 'nodi', relationships: 'relazioni', judgments: 'sentenze', acts: 'atti normativi' }
    : { nodes: 'nodes', relationships: 'relationships', judgments: 'judgments', acts: 'regulatory acts' };
  const sources = isIt ? [
    ['Normativa italiana', '75.000 atti primari e 47.000 atti regionali da tutte le regioni.', BookOpen],
    ['Giurisprudenza', 'Cassazione, TAR, Consiglio di Stato, Corti europee e altre giurisdizioni.', Gavel],
    ['Diritto europeo', '71.000 regolamenti e direttive UE, Corte di Giustizia e Corte EDU.', Globe2],
    ['Autorità e contratti', 'EDPB, Banca d’Italia, Consob, IVASS e CCNL dalla banca dati CNEL.', Building2],
  ] : [
    ['Italian legislation', '75,000 primary acts and 47,000 regional acts from every region.', BookOpen],
    ['Case law', 'Cassation, regional administrative courts, Council of State, European courts and more.', Gavel],
    ['European law', '71,000 EU regulations and directives, CJEU and ECHR case law.', Globe2],
    ['Authorities and contracts', 'EDPB, Bank of Italy, Consob, IVASS and collective agreements from CNEL.', Building2],
  ];

  return (
    <section id="data-sources" className="border-t border-white/10 py-16 md:py-24 bg-void-blue">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
        <div className="mb-10 md:mb-12 max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 md:mb-6">
            {isIt ? 'Le fonti di LegisGraph' : 'LegisGraph data sources'}
          </h2>
          <p className="text-base md:text-lg text-gray-300 leading-relaxed">
            {isIt ? 'Un corpus giuridico basato su fonti ufficiali, aggiornato quotidianamente.' : 'A legal corpus based on official sources and updated daily.'}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {LEGISGRAPH_METRICS.map(({ value, key }) => (
            <div key={key} className="glass-panel rounded-2xl border border-white/10 p-5 md:p-6">
              <strong className="block text-2xl md:text-3xl font-bold text-gradient-ai">{value}</strong>
              <span className="mt-1 block text-sm md:text-base leading-snug text-gray-400">{metricLabels[key]}</span>
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {sources.map(([title, text, Icon]) => (
            <article key={title as string} className="glass-panel rounded-2xl p-6 md:p-8 flex flex-col border border-white/5">
              <Icon className="mb-5 text-mystic-violet" size={32} />
              <h3 className="text-xl font-bold text-white mb-3">{title as string}</h3>
              <p className="text-base leading-relaxed text-gray-400">{text as string}</p>
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-6 rounded-2xl border border-white/10 bg-white/[0.04] p-6 md:p-8 md:flex-row md:items-center">
          <div>
            <h3 className="text-xl font-bold text-white">{t('need_a_different_data_source')}</h3>
            <p className="mt-2 text-base leading-relaxed text-gray-400">{t('subtitle_need_a_different_data_source')}</p>
          </div>
          <Button variant="gradient" size="lg" icon={<Mail size={20} />} onClick={() => { window.location.href = 'mailto:info@legisratio.it'; }}>
            {t('contact_us')}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default OurDataSourcesSection;
